import { useCallback, useEffect, useRef } from "react";

import { useLocalStorage } from "lib/hooks/useLocalStorage";

const OPEN_STORAGE_KEY = "verticalNavExpanded";
const PINNED_STORAGE_KEY = "verticalNavPinned";

type SideNavOpenStateOptions = {
  // True on tiers where the side nav is visible but not persistent (1024–1439): collapse on every
  // navigate unless pinned.
  collapseOnNavigate: boolean;
  // Current SPA route path; undefined on legacy full-reload pages (mount-time check is the whole
  // behaviour there).
  routePathname?: string;
};

/**
 * Owns the side nav's open/pinned policy in the nav layer, driving SidebarProvider in controlled
 * mode. `pinned` is separate from `open`: `open` persists across navigation; `pinned` decides
 * whether a route change overrides it back to collapsed.
 */
export function useSideNavOpenState({
  collapseOnNavigate,
  routePathname,
}: SideNavOpenStateOptions) {
  const [open, setOpen] = useLocalStorage(OPEN_STORAGE_KEY, true);
  const [pinned, setPinned] = useLocalStorage(PINNED_STORAGE_KEY, false);

  const hasMountedRef = useRef(false);
  const prevRoutePathnameRef = useRef(routePathname);

  // A user-driven open/close. On collapsing tiers the existing toggle doubles as the pin control:
  // expanding pins, collapsing unpins. No separate affordance.
  const onOpenChange = useCallback(
    (next: boolean | ((current: boolean) => boolean)) => {
      setOpen((current) => {
        const value = typeof next === "function" ? next(current) : next;
        if (collapseOnNavigate) setPinned(value);
        return value;
      });
    },
    [collapseOnNavigate, setOpen, setPinned],
  );

  // Collapse on mount and on every real route change, unless pinned. Writes through `setOpen`, not
  // `onOpenChange`, so it doesn't touch `pinned` (a collapse isn't a user action). Deps exclude
  // `pinned`/`setOpen` on purpose: toggling the pin, or a resize-only tier flip, must not collapse.
  useEffect(() => {
    const isFirstRun = !hasMountedRef.current;
    const pathnameChanged = prevRoutePathnameRef.current !== routePathname;
    hasMountedRef.current = true;
    prevRoutePathnameRef.current = routePathname;

    if (!collapseOnNavigate) return;
    if (!isFirstRun && !pathnameChanged) return;
    if (!pinned) setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapseOnNavigate, routePathname]);

  return { open, onOpenChange };
}
